import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTracking } from '@jg/react-tracking';
import Loading from '@jg/loading';
import { Trans } from 'react-i18next';

import Alert from '@jg/alert';
import { REQUEST } from '../../consts';
import CaptainCard from '../CaptainCard/CaptainCard';
import MemberCard from './MemberCard';
import UnlinkMemberModal from '../UnlinkMemberModal/UnlinkMemberModal';
import fetchMembersList from '../../actions/thunks/fetchMembersList';
import unlinkMember from '../../actions/thunks/unlinkMember';
import { resetRequestError } from '../../actions/actionCreators/httpActions';
import {
  getSortedMembersList,
  getIsCaptainInMembersList,
  getCaptain,
} from '../../selectors/apiSelectors';
import {
  getRequestInFlight,
  getRequestError,
} from '../../selectors/httpSelectors';

import {
  IMember,
  ICaptain,
  IAppState,
  IMembersListData,
} from '../../types/state.d';

type Props = MapDispatchToProps & MapStateToProps;

export const MemberCardList = ({
  isCaptainInMembersList,
  sortedMembersList,
  captain,
  isInFlight,
  isError,
  isUnlinkingError,
  ...props
}: Props) => {
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [memberToBeUnlinked, setMemberToBeUnlinked] = useState<
    IMember | undefined
  >(undefined);

  const track = useTracking();

  useEffect(() => {
    props.fetchMembersList();
  }, []);

  const onOpenUnlinkModal = (member: IMember) => {
    setMemberToBeUnlinked(member);
    setIsUnlinkModalOpen(true);
    track({
      action_name: 'click',
      subtype: 'team member',
      event: 'click',
      event_value: 'unlink',
      page_section: 'admin > members',
    });
  };

  if (isInFlight) {
    return (
      <>
        <Loading size="small" />
        <span className="jg-sr-only" aria-live="polite">
          <Trans i18nKey="content.editteam.loading.sronly" />
        </span>
      </>
    );
  }

  return (
    <>
      <ul className="jg-tc-ul-unstyled jg-space-pbxl jg-space-mbn">
        {!isCaptainInMembersList && <CaptainCard captain={captain} />}
        {sortedMembersList.map((member: IMember) => {
          const isCaptain = captain.userGuid === member.userGuid;
          return (
            <MemberCard
              key={member.fundraisingPageGuid}
              isCaptain={isCaptain}
              onClick={() => onOpenUnlinkModal(member)}
              member={member}
              canUnlinkMember={sortedMembersList.length > 1}
            />
          );
        })}
      </ul>

      {memberToBeUnlinked && (
        <UnlinkMemberModal
          onCloseModal={() => setIsUnlinkModalOpen(false)}
          isModalOpen={isUnlinkModalOpen}
          unlinkMember={props.unlinkMember}
          member={memberToBeUnlinked}
          isInFlight={isInFlight}
          isError={isUnlinkingError}
        />
      )}
      {isError && (
        <Alert type="error" displayType="inline" onClose={resetRequestError}>
          <Trans i18nKey="global.error.fatal" />
        </Alert>
      )}
    </>
  );
};

type MapStateToProps = {
  sortedMembersList: IMembersListData['members'];
  isInFlight?: boolean;
  isError: boolean;
  isUnlinkingError: boolean;
  isCaptainInMembersList: boolean;
  captain: ICaptain;
};
type MapDispatchToProps = {
  resetRequestError: typeof resetRequestError;
  fetchMembersList: typeof fetchMembersList;
  unlinkMember: typeof unlinkMember;
};

const mapStateToProps = (state: IAppState): MapStateToProps => ({
  sortedMembersList: getSortedMembersList(state),
  isInFlight: getRequestInFlight(REQUEST.GET_MEMBERS_LIST)(state),
  isError: getRequestError(REQUEST.GET_MEMBERS_LIST)(state),
  isUnlinkingError: getRequestError(REQUEST.UNLINK_MEMBER)(state),
  isCaptainInMembersList: getIsCaptainInMembersList(state),
  captain: getCaptain(state),
});

const mapDispatchToProps: MapDispatchToProps = {
  resetRequestError,
  fetchMembersList,
  unlinkMember,
};
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MemberCardList);
